/**
 * Backend-executed on-chain transfers (`hl_deposit` / `external_withdraw`).
 *
 * These are asynchronous: `POST /v1/wallet/transfers` only queues a row, and a
 * backend worker signs, broadcasts and confirms it over the following seconds
 * to minutes. The 200 from the submit therefore says nothing about whether the
 * money moved — a transfer can still fail afterwards (drained gas treasury,
 * an underfunded wallet, a bridge rejection), and for deposits the bridge
 * swallows its own failures, so even a mined transaction can credit nothing.
 *
 * This hook is the read side of that: poll while anything is in flight, stop
 * when nothing is. `TransferStatusSync` turns the transitions into user-facing
 * outcomes.
 */
import { useQuery } from "@tanstack/react-query";
import {
	listTransfers,
	type Transfer,
	type TransferStatus,
} from "#/lib/api/wallet";
import { queryKeys } from "./query-keys";
import { useWallet } from "./use-wallet";

/**
 * Statuses the backend can still move away from. Everything else is terminal
 * (`confirmed` / `failed`) — see the transfers worker's state machine.
 */
const PENDING_STATUSES: readonly TransferStatus[] = [
	"pending",
	"topping_up",
	"sent",
];

export function isTransferPending(t: Transfer): boolean {
	return PENDING_STATUSES.includes(t.status);
}

/**
 * How often to re-check while a transfer is in flight. The backend worker
 * ticks every 10s and a deposit needs a few confirmations, so this is a
 * couple of polls per worker tick — enough to feel responsive without
 * hammering the API. Polling stops entirely once nothing is pending.
 */
export const TRANSFER_POLL_INTERVAL_MS = 5_000;

/**
 * Poll only while something is actually in flight — `false` stops the timer.
 * Every user has at most one active transfer (the backend enforces it), so
 * this is idle for all but a few minutes of a session.
 */
export function nextPollInterval(
	transfers: Transfer[] | undefined,
): number | false {
	return transfers?.some(isTransferPending) ? TRANSFER_POLL_INTERVAL_MS : false;
}

export function useWalletTransfers() {
	const { address, isConnected } = useWallet();

	return useQuery({
		queryKey: queryKeys.wallet.transfers(address),
		queryFn: () => listTransfers(),
		enabled: isConnected,
		// Always refetched on demand — a stale in-flight status is the whole
		// thing we're trying to avoid showing.
		staleTime: 0,
		refetchInterval: (query) => nextPollInterval(query.state.data),
		// A backgrounded tab still needs to catch the terminal state, so the
		// arrival toast isn't silently missed.
		refetchIntervalInBackground: true,
	});
}
